import { useEffect, useState } from 'react';

import { Times } from './icons.js';

import type { QuarantinedMutation } from '../core/db/schema.js';

/**
 * Разбор отвергнутых правок.
 *
 * Индикатор «N правок не приняты сервером» честно говорил, что что-то
 * потеряно, но не говорил **что** — и не давал ни посмотреть, ни выбросить.
 * Тупик: правки лежат в карантине в локальной базе и сами оттуда не денутся,
 * а счётчик висит вечно. Отсюда этот разбор.
 *
 * Восстановить правку нечем и не предлагается: своей версии записи у нас уже
 * нет — она откатилась в момент отказа, иначе на экране осталась бы задача,
 * которой нет ни на сервере, ни на других устройствах (решение этапа 9).
 * Поэтому единственное действие здесь — выбросить, и оно в два шага.
 */

/**
 * Причины отказа человеческими словами. Коды приходят с сервера
 * (`sync/apply.ts`), и показывать их как есть — то же самое, что показывать
 * «422»: правда, из которой ничего не следует.
 */
const REASONS: Record<string, string> = {
  duplicate: 'такая запись уже есть',
  missing_parent: 'задача, к которой она относится, не найдена',
  missing_field: 'в правке не хватает обязательного поля',
  invalid_value: 'значение не подошло по типу или ограничению',
  unknown_entity: 'сервер не знает такой сущности',
  invalid_mutation: 'сервер не смог применить правку',
  // Единственная причина, которую человек может устранить сам, — поэтому она и
  // названа действием, а не диагнозом.
  hlc_ahead: 'часы устройства убежали вперёд — проверьте дату и время',
};

/** Поля в правке называются так же, как на экране: код поля здесь ничего не объясняет. */
const FIELDS: Record<string, string> = {
  title: 'название',
  notes: 'описание',
  status: 'статус',
  sprint: 'спринт',
  due: 'срок',
  due_time: 'время срока',
  repeat: 'повтор',
  series_id: 'серия повтора',
  tag: 'тег',
  text: 'текст',
  done: 'отметка',
  parent_id: 'проект',
  number: 'номер',
};

/** Длинное описание целиком в список не лезет, а начала хватает, чтобы узнать правку. */
const VALUE_LIMIT = 120;

/**
 * Что было в отвергнутой правке.
 *
 * Без этого разбор показывал только вид записи и причину: «правка: задача —
 * значение не подошло». Восстановить правку нечем, и единственное, что
 * остаётся человеку, — перенабрать её; для этого надо хотя бы видеть, что в
 * ней было. Служебные поля отсюда убраны: они ничего не говорят, а строку
 * занимают.
 */
export function describeContents(entry: QuarantinedMutation): string | undefined {
  const parts = Object.entries(entry.mutation.fields)
    .filter(([name, value]) => !HIDDEN.has(name) && value !== null && value !== '')
    .map(([name, value]) => `${FIELDS[name] ?? name}: ${trim(String(value))}`);

  return parts.length === 0 ? undefined : parts.join(' · ');
}

const HIDDEN = new Set([
  'created_at',
  'updated_at',
  'done_at',
  'deleted_at',
  'order_key',
  'task_id',
]);

function trim(value: string): string {
  return value.length > VALUE_LIMIT ? `${value.slice(0, VALUE_LIMIT)}…` : value;
}

const ENTITIES: Record<string, string> = {
  task: 'задача',
  checklist_item: 'пункт чек-листа',
  task_link: 'связь',
  task_tag: 'тег',
  settings: 'настройки',
};

export function describeRejection(entry: QuarantinedMutation): string {
  const what = ENTITIES[entry.mutation.entity] ?? entry.mutation.entity;
  const how = entry.mutation.kind === 'delete' ? 'удаление' : 'правка';
  // Незнакомый код показывается как есть: подменять его общей фразой значит
  // менять одну непрозрачность на другую.
  const why = REASONS[entry.reason] ?? entry.reason;

  return `${how}: ${what} — ${why}`;
}

export function Rejected({
  entries,
  onForget,
  onClose,
}: {
  entries: readonly QuarantinedMutation[];
  onForget: () => void;
  onClose: () => void;
}): React.ReactElement {
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!armed) return undefined;
    const timer = setTimeout(() => setArmed(false), 4_000);
    return () => clearTimeout(timer);
  }, [armed]);

  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') onClose();
    }
    globalThis.addEventListener('keydown', onKey);
    return () => globalThis.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="overlay">
      <button
        type="button"
        className="overlay__backdrop"
        aria-label="Закрыть разбор"
        onClick={onClose}
      />

      <aside className="panel" role="dialog" aria-label="Непринятые правки">
        <header className="panel__head">
          <span className="panel__crumb">Сервер не принял эти правки</span>
          <button type="button" className="iconbtn" aria-label="Закрыть" onClick={onClose}>
            <Times />
          </button>
        </header>

        <div className="panel__body">
          {entries.length === 0 ? (
            <p className="links__empty">Непринятых правок нет.</p>
          ) : (
            <>
              <ul className="links__list" aria-label="Непринятые правки">
                {entries.map((entry) => {
                  const contents = describeContents(entry);
                  return (
                    <li key={entry.op_id} className="links__item rejected__item">
                      <span>{describeRejection(entry)}</span>
                      {contents !== undefined && <span className="rejected__what">{contents}</span>}
                    </li>
                  );
                })}
              </ul>

              <p className="picker__empty">
                Применить их заново нельзя — сервер уже отказался. Что в них было, написано выше:
                это всё, что от них осталось, и перенабрать придётся руками. Здесь можно только
                перестать о них помнить.
              </p>
            </>
          )}
        </div>

        {entries.length > 0 && (
          <footer className="panel__foot">
            <button
              type="button"
              className="danger"
              onClick={() => {
                if (armed) onForget();
                else setArmed(true);
              }}
            >
              {armed ? 'Точно выбросить?' : 'Выбросить'}
            </button>
          </footer>
        )}
      </aside>
    </div>
  );
}
